import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { FinalCTA } from '../components/FinalCTA';
import { ArrowUpRight } from 'lucide-react';
import { useProjects } from '../hooks/useProjects';
import { useSearchParams } from 'react-router-dom';
const categories = ['Todos', 'Oficinas corporativas', 'Sucursales bancarias', 'Retail y tiendas', 'Educación / institucional', 'Industrial / especial', 'Otros'];
export function ProjectsPage() {
  const {
    projects
  } = useProjects();
  const [searchParams] = useSearchParams();
  const filterParam = searchParams.get('filter');
  const [activeFilter, setActiveFilter] = useState(filterParam || 'Todos');
  // Update filter when URL parameter changes
  useEffect(() => {
    if (filterParam && categories.includes(filterParam)) {
      setActiveFilter(filterParam);
    }
  }, [filterParam]);
  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const filteredProjects = activeFilter === 'Todos' ? projects : projects.filter(project => project.category === activeFilter);
  return <div className="min-h-screen bg-white font-sans text-gray-900 selection:bg-blue-600 selection:text-white" data-id="element-916">
      <Header data-id="element-917" />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-48 lg:pb-24 overflow-hidden" data-id="element-918">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-100 via-gray-50 to-white opacity-70" data-id="element-919"></div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center" data-id="element-920">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-921">
            <span className="mb-4 inline-block rounded-full bg-blue-100 px-4 py-1.5 text-sm font-bold text-blue-600" data-id="element-922">
              PORTAFOLIO
            </span>
            <h1 className="mb-6 text-5xl font-extrabold tracking-tight text-gray-900 sm:text-6xl md:text-7xl" data-id="element-923">
              Nuestras Obras Terminadas
            </h1>
            <p className="mx-auto max-w-3xl text-xl text-gray-600 leading-relaxed mb-4" data-id="element-924">
              Más de 160 proyectos ejecutados, sobre 75.000 metros cuadrados
              entregados y sobre 1.000.000 de UF en contratos administrados para
              más de 35 clientes... y contando.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="pb-12" data-id="element-925">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-926">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4" data-id="element-927">
            {categories.map(category => <button key={category} onClick={() => setActiveFilter(category)} className={`relative px-4 py-2 sm:px-6 sm:py-3 rounded-full text-xs sm:text-sm font-bold transition-all duration-300 ${activeFilter === category ? 'text-white shadow-lg shadow-blue-500/30' : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'}`} data-id="element-928">
                {activeFilter === category && <motion.div layoutId="activeTab" className="absolute inset-0 bg-blue-600 rounded-full" transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-929" />}
                <span className="relative z-10" data-id="element-930">{category}</span>
              </button>)}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="pb-24 bg-white" data-id="element-931">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-932">
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-933">
            <AnimatePresence mode="popLayout" data-id="element-934">
              {filteredProjects.map(project => <motion.div layout key={project.id} initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} transition={{
              duration: 0.4
            }} className="group relative flex flex-col h-full bg-white rounded-[2rem] overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-blue-900/10 transition-all duration-300 border border-gray-100" data-id="element-935">
                  {/* Image Container */}
                  <div className="relative aspect-[4/3] overflow-hidden" data-id="element-936">
                    <img src={project.images[0]} alt={project.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-937" />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-938" />

                    {/* Category Badge */}
                    <div className="absolute top-4 left-4" data-id="element-939">
                      <span className="inline-block rounded-full bg-white/90 backdrop-blur-sm px-3 py-1 text-xs font-bold text-blue-900 shadow-sm" data-id="element-940">
                        {project.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col flex-grow p-6" data-id="element-941">
                    <div className="flex justify-between items-start mb-2" data-id="element-942">
                      <h3 className="text-xl font-bold text-gray-900 leading-tight group-hover:text-blue-600 transition-colors" data-id="element-943">
                        {project.name}
                      </h3>
                      <ArrowUpRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-colors shrink-0 ml-2" data-id="element-944" />
                    </div>
                    <p className="text-gray-500 text-sm mb-4 flex-grow" data-id="element-945">
                      {project.description}
                    </p>
                    <div className="pt-4 border-t border-gray-100 flex justify-between items-center text-xs sm:text-sm" data-id="element-946">
                      <div className="flex gap-3 text-gray-400 font-semibold uppercase tracking-wider" data-id="element-947">
                        <span data-id="element-948">{project.year}</span>
                        <span data-id="element-949">•</span>
                        <span data-id="element-950">{project.m2} m²</span>
                        <span data-id="element-951">•</span>
                        <span data-id="element-952">{project.comuna}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>)}
            </AnimatePresence>
          </motion.div>

          {filteredProjects.length === 0 && <div className="text-center py-20" data-id="element-953">
              <p className="text-xl text-gray-500" data-id="element-954">
                No se encontraron obras en esta categoría.
              </p>
            </div>}
        </div>
      </section>

      <FinalCTA data-id="element-955" />
      <Footer data-id="element-956" />
    </div>;
}